import { Modal, ModalBody, ModalFooter, ModalHeader } from 'baseui/modal'
import React, { useState } from 'react'
import classNames from 'classnames'
import { WemoloButton, WemoloInfoIcon, WemoloTypography } from '../../../components/Wemolo'
import { DETECTION_EXPLORER_TEXT } from '../config'
import { estimateTransferBytes, formatBytes } from '../estimateTransfer'
import { ImageType } from '../request_images'
import type { ImageCoverage } from './ActionBarContainer'
import type { ExportMode } from '../useExportDetections'
import { AssetTypePicker } from './AssetTypePicker'
import styles from './ActionModal.module.css'

const { exportModal, retrieveModal } = DETECTION_EXPLORER_TEXT

// baseui's Modal Root has no z-index (it expects a LayersManager, which this app doesn't use),
// so it would sit below the filters bar (z-index:2), the sticky table header (z-index:1) and the
// filter dropdowns (z-index:1000). Lift the whole layer above all page chrome.
const MODAL_OVERRIDES = { Root: { style: { zIndex: 1100 } } } as const

const MODE_OPTIONS: ReadonlyArray<{ mode: ExportMode; title: string; description: string; needsImages: boolean }> = [
    { mode: 'detections', ...exportModal.detectionsOnly, needsImages: false },
    { mode: 'images', ...exportModal.imagesOnly, needsImages: true },
    { mode: 'all', ...exportModal.all, needsImages: true }
]

// The imageless-rows warning sentence, plus the pending clause when a retrieval is already running.
const buildImageWarning = (coverage: NonNullable<ImageCoverage>): string => {
    if (coverage.kind === 'estimate') return exportModal.partialImagesEstimate
    const base = exportModal.partialImages(coverage.missing, coverage.total)
    return coverage.pending > 0 ? `${base} ${exportModal.partialImagesPending(coverage.pending)}` : base
}

export type ModalAction = 'export' | 'retrieve'

type Props = {
    /** Which flow the modal is showing, or null when closed. */
    action: ModalAction | null
    onClose: () => void
    /** Detections in scope (selected count, or the full total). */
    count: number
    /** Whether the scope is the current selection (export wording). */
    isSelection: boolean
    /** Whether any in-scope detection has images — gates export's image modes. */
    hasImages: boolean
    /**
     * Whether some in-scope rows lack images (they'd be skipped from a ZIP export). Exact counts for
     * a selection, a generic flag for a full export, or null when there's nothing to warn about.
     */
    imageCoverage?: ImageCoverage
    /** The active flow's request is in flight. */
    isBusy: boolean
    onExport: (mode: ExportMode, imageTypes: ImageType[]) => void
    onRetrieve: (imageTypes: ImageType[]) => void
}

export const ActionModal: React.FC<Props> = ({
    action,
    onClose,
    count,
    isSelection,
    hasImages,
    imageCoverage,
    isBusy,
    onExport,
    onRetrieve
}) => {
    const [mode, setMode] = useState<ExportMode>('detections')
    const [imageTypes, setImageTypes] = useState<ImageType[]>([])

    const isExport = action === 'export'
    // Export reveals the picker only for image-including modes; retrieve always needs types.
    const isImageMode = mode === 'images' || mode === 'all'
    const needsTypes = action === 'retrieve' || (isExport && isImageMode)
    const canConfirm = !isBusy && (!needsTypes || imageTypes.length > 0)
    // Warn about imageless rows only when they'd actually be dropped — i.e. an image-including
    // export mode is selected and some in-scope row has no images.
    const imageWarning = isExport && isImageMode && imageCoverage ? buildImageWarning(imageCoverage) : null

    const reset = () => {
        setMode('detections')
        setImageTypes([])
    }
    const handleClose = () => {
        reset()
        onClose()
    }
    const handleConfirm = () => {
        if (action === 'export') onExport(mode, imageTypes)
        else if (action === 'retrieve') onRetrieve(imageTypes)
        // Retrieve closes itself on success; export closes immediately, so reset either way.
        reset()
    }

    const scopeNoun = isSelection ? exportModal.selectedDetections : exportModal.allDetections
    const scopeLabel = isSelection ? `${count} ${scopeNoun}` : `All ${count} ${scopeNoun}`

    return (
        <Modal isOpen={action !== null} onClose={handleClose} animate closeable overrides={MODAL_OVERRIDES}>
            {action && (
                <>
                    <ModalHeader>{isExport ? exportModal.title : retrieveModal.title}</ModalHeader>
                    <ModalBody>
                        {isExport ? (
                            <>
                                <WemoloTypography variant="body-small" tag="p" className={styles.scope}>
                                    {scopeLabel}
                                </WemoloTypography>
                                <div className={styles.options}>
                                    {MODE_OPTIONS.map((option) => {
                                        const disabled = option.needsImages && !hasImages
                                        return (
                                            <button
                                                key={option.mode}
                                                type="button"
                                                className={classNames(
                                                    styles.option,
                                                    mode === option.mode && styles.optionSelected
                                                )}
                                                onClick={() => setMode(option.mode)}
                                                disabled={disabled}
                                                aria-pressed={mode === option.mode}
                                            >
                                                <WemoloTypography variant="label-medium" tag="span">
                                                    {option.title}
                                                </WemoloTypography>
                                                <WemoloTypography
                                                    variant="body-small"
                                                    tag="span"
                                                    className={styles.optionDescription}
                                                >
                                                    {disabled ? exportModal.noImagesHint : option.description}
                                                </WemoloTypography>
                                            </button>
                                        )
                                    })}
                                </div>
                                {imageWarning && (
                                    <div className={styles.imageWarning} role="status">
                                        <WemoloInfoIcon className={styles.imageWarningIcon} />
                                        <WemoloTypography
                                            variant="body-small"
                                            tag="p"
                                            className={styles.imageWarningText}
                                        >
                                            {imageWarning}
                                        </WemoloTypography>
                                    </div>
                                )}
                            </>
                        ) : (
                            <WemoloTypography variant="body-small" tag="p" className={styles.intro}>
                                {retrieveModal.intro} {count} {retrieveModal.detections}.
                            </WemoloTypography>
                        )}

                        {needsTypes && (
                            <AssetTypePicker
                                value={imageTypes}
                                onChange={setImageTypes}
                                disabled={isBusy}
                                className={styles.picker}
                            />
                        )}

                        {action === 'retrieve' && (
                            <div className={styles.estimate}>
                                <WemoloTypography variant="label-small" tag="span" className={styles.estimateLabel}>
                                    {retrieveModal.estimatedTransfer}
                                </WemoloTypography>
                                <WemoloTypography variant="body-small" tag="span">
                                    {formatBytes(estimateTransferBytes(count, imageTypes))}
                                </WemoloTypography>
                            </div>
                        )}
                    </ModalBody>
                    <ModalFooter>
                        <div className={styles.footer}>
                            <WemoloButton view="secondary" onClick={handleClose}>
                                {isExport ? exportModal.cancel : retrieveModal.cancel}
                            </WemoloButton>
                            <WemoloButton view="primary" disabled={!canConfirm} onClick={handleConfirm}>
                                {isExport ? exportModal.confirm : retrieveModal.confirm}
                            </WemoloButton>
                        </div>
                    </ModalFooter>
                </>
            )}
        </Modal>
    )
}
